import type { ExperimentChecker } from "./ExperimentChecker";
import { checkExperimentBase } from "./ExperimentChecker";
import {
  staticRibbonExperiments,
  isShyRibbonFeatureEnabled as isShyRibbonFeatureEnabledGeneric,
} from "./staticRibbonExperiments";
import { fluentExperiments } from "./fluentExperiments";

const react18FGates = {
  react18RenderTooltipFixEnabled: "SharedOnline.React18RenderTooltipFixEnabled",
  react18RenderHeaderButtonFixEnabled:
    "Microsoft.Office.SharedOnline.React18RenderHeaderButtonFixEnabled",
  react18RenderDialogEnabled:
    "Microsoft.Office.SharedOnline.React18RenderDialogEnabled",
};

const react18CGates = {
  react18RenderCanvasContextualTooltipsEnabled:
    "SharedOnline.ChangeGate.React18RenderCanvasContextualTooltipsEnabled",
};

const react18Experiments = {
  ...react18FGates,
  ...react18CGates,
};

// List of all shield change/feature gates that must be checked at the acui level. If your fix can be checked at the ooux level or app level, use ShieldSettings.ts
const shieldExperiments = {
  wideBorderForSelectedButtonEnabled:
    "SharedOnline.ChangeGate.WideBorderForSelectedButtonEnabled",
  shouldFocusOnContainerEnabled:
    "SharedOnline.ChangeGate.ShouldFocusOnContainerEnabled",
  shouldDisableSplitButtonWhenMenuIsDisabledEnabled:
    "SharedOnline.ChangeGate.ShouldDisableSplitButtonWhenMenuIsDisabledEnabled",
  hideReact18SettingsPlaceholderEnabled:
    "SharedOnline.ChangeGate.HideReact18SettingsPlaceholderEnabled",
  resetDialogRootAfterUnmountEnabled:
    "SharedOnline.ChangeGate.ResetDialogRootAfterUnmountEnabled",
  onlyRenderTabStripForVisiblePanesEnabled:
    "SharedOnline.ChangeGate.onlyRenderTabStripForVisiblePanesEnabled",
  ...react18Experiments,
};

const consumerExperiments = {
  displayFreeFontsFirstFlightEnabled:
    "Microsoft.Office.WordOnline.DisplayFreeFontsFirst",
  isProTipSuggestionEnabled:
    "Microsoft.Office.WordOnline.ProTipSuggestionEnabled",
};

const unAuthExperiments = {
  isSISURibbonEnabled: "IsSISURibbonEnabled",
};

export const ribbonExperiments = {
  renderInitiallyEnabledRibbonIsEnabled:
    "RenderInitiallyEnabledRibbonIsEnabled",
  updatedTellMePlaceholderIsEnabled: "UpdatedTellMePlaceholderIsEnabled",
  multilineRibbonIsEnabled: "ReactMultilineRibbonIsEnabled",
  taskPaneResizingIsEnabled: "TaskPaneResizingIsEnabled",
  activateComboAndSpinControlOnEnter:
    "ActivateComboAndSpinMenuItemOnEnterIsEnabled",
  chatPPTEnabled: "Microsoft.Office.PowerPointOnline.ChatPPTOnline",
  ribbonCachedScalingIsEnabled: "ReactRibbonCachedScalingIsEnabled",
  disableRibbonAnimationComponents: "DisableAnimationComponentsIsEnabled",
  delayInstallAddInIsEnabled: "DelayInstallAddInIsEnabled",
  tooltipsTargetOverflowSetItemEnabled: "TooltipsTargetOverflowSetItemEnabled",
  disableColorPickerFancyTooltips: "DisableColorPickerFancyTooltips",
  isDisabledTooltipsEnabled: "SharedOnline.DisabledTooltipsEnabled",
  tooltipNotDismissingAfterUnmountFixEnabled:
    "SharedOnline.ChangeGate.TooltipNotDismissingAfterUnmountFixEnabled",
  batchTopBarScaleDataInRibbon: "BatchTopBarScaleDataInRibbon",
  decentralizedScalingLogicIsEnabled:
    "Microsoft.Office.SharedOnline.DecentralizedScalingLogicIsEnabled",
  perfMarkerThrottlingOverrideIsEnabled:
    "Microsoft.Office.SharedOnline.PerfMarkerThrottlingOverrideIsEnabled",
  shouldContextualTabUseNormalTabStyles:
    "ShouldContextualTabUseNormalTabStyles",
  zoomSliderIsEnabled: "ZoomSliderIsEnabled",
  customAnonymousNameIsEnabled:
    "Microsoft.Office.SharedOnline.IsCustomAnonymousNameEnabled",
  useFullDescriptionLabelOnly:
    "SharedOnline.ChangeGate.UseFullDescriptionLabelOnly",
  appTooltipAppContextIsEnabled:
    "SharedOnline.ChangeGate.AppTooltipAppContextIsEnabled",
  headerIcon: "SharedOnline.ChangeGate.HeaderIcon",
  singleLineToolBar: "SharedOnline.IsSingleLineToolbarEnabled",
  appGalleryPreviewButtonIdIsEnabled: "AppGalleryPreviewButtonIdIsEnabled",
  mfpRemoveUnnecessaryRequestAnimationFrame:
    "Microsoft.Office.SharedOnline.ModernFontPickerRemoveUnnecessaryRequestAnimationFrameIsEnabled",
  mfpDeferOnMenuDismissedEnabled:
    "Microsoft.Office.SharedOnline.ModernFontPickerDeferOnMenuDismissedIsEnabled",
  mfpSubmenuCloseItemKeyCheckEnabled:
    "SharedOnline.ChangeGate.ModernFontPickerSubmenuCloseItemKeyCheckIsEnabled",
  copilotMaxTaskpaneWidthIsEnabled:
    "Microsoft.Office.WordOnline.CopilotTaskPaneMaxWidth",
  messageBarUpdatesEnabled: "SharedOnline.ChangeGate.MessageBarUpdatesEnabled",
  isNewFluentIconsEnabled: "Microsoft.Office.SharedOnline.FluentIconsEnabled",
  v9MenuAutoDismissFixEnabled:
    "SharedOnline.ChangeGate.v9MenuAutoDismissFixEnabled",
  fixOnMenuOpenedTriggerOnSubmenuHover:
    "SharedOnline.ChangeGate.FixOnMenuOpenedTriggerOnSubmenuHover",
  isFluentV9DispatchOptimizationEnabled:
    "SharedOnline.FluentV9DispatchOptimizationEnabled",
  isFluentV9FloatieDispatchOptimizationEnabled:
    "SharedOnline.FluentV9FloatieDispatchOptimizationEnabled",
  triggerOnMenuDismissedOnSubmenuClose:
    "SharedOnline.ChangeGate.TriggerOnMenuDismissedOnSubmenuClose",
  isCloseBtnTabKeyDownOnResizeHandleEnabled:
    "SharedOnline.ChangeGate.isCloseBtnTabKeyDownOnResizeHandleEnabled",
  fluentV9ColorPickerIsEnabled: "SharedOnline.FluentV9ColorPickerEnabled",
  fluentV9PreviewGalleryIsEnabled: "SharedOnline.FluentV9PreviewGalleryEnabled",
  insertTableGridFluentV9IsEnabled:
    "SharedOnline.InsertTableGridFluentV9IsEnabled",
  isShadowOverflowFixEnabled:
    "SharedOnline.ChangeGate.IsShadowOverflowFixEnabled",
  isConditionForV9StandaloneCMEnabled:
    "SharedOnline.ChangeGate.IsConditionForV9StandaloneCMEnabled",
  isStopKeyDownEventPropagationInV9MenuEnabled:
    "SharedOnline.ChangeGate.StopKeyDownEventPropagationInV9MenuEnabled",
  isContextualApiTelemetryLevelUpdateEnabled:
    "SharedOnline.ChangeGate.ContextualApiTelemetryLevelUpdateEnabled",
  trackOOUXAsyncUiEnabled: "TrackOOUXAsyncUiEnabled",
  isHighContrastModeEnabled: "SharedUXHighContrastThemeIsEnabled",
  isHighContrastThemeOn: "HighContrastThemeIsOn",
  isV9ContextMenuPositionFixEnabled:
    "SharedOnline.isV9ContextMenuPositionFixEnabled",
  isV8AppContextMenuSlideFixEnabled:
    "SharedOnline.isV8AppContextMenuSlideFixEnabled",
  openToDesktopInL1Enabled: "SharedOnline.OpenToDesktopInL1Enabled",
  openToDesktopInL2ExportEnabled: "SharedOnline.OpenToDesktopInL2ExportEnabled",
  isContextMenuAndFloatieRightClickDisabled:
    "SharedOnline.ChangeGate.ContextMenuAndFloatieRightClickDisabled",
  isSplitButtonIgnoreFastDisableEnabled:
    "SharedOnline.IsSplitButtonIgnoreFastDisableEnabled",
  isFavoritesInFileMenuEnabled: "SharedOnline.IsFavoritesInFileMenuEnabled",
  appFlyoutAnchorRestoreFocusFixEnabled:
    "SharedOnline.AppFlyoutAnchorRestoreFocusFixEnabled",
  headlessThemingEnabled: "SharedOnline.HeadlessThemingEnabled",
  floatieDismissOnCtrlFixEnabled: "SharedOnline.FloatieDismissOnCtrlFixEnabled",
  isAccessAndAcceleratorKeyMappingEnabled:
    "Microsoft.Office.SharedOnline.IsAccessAndAcceleratorKeyMappingEnabled",
  isCommandCenterEnabled: "SharedOnline.IsCommandCenterEnabled",
  isCopilotFrontierEnabled: "SharedOnline.FrontierSettingEnabled",
  isToggleKeytipFixEnabled: "SharedOnline.ChangeGate.ToggleKeytipFixEnabled",
  isBaseCalloutTargetQuerySelectorEnabled:
    "Microsoft.Office.SharedOnline.ChangeGate.isBaseCalloutTargetQuerySelectorEnabled",
  logTaskPaneWidthInfoIsEnabled:
    "SharedOnline.ChangeGate.LogTaskPaneWidthInfoIsEnabled",
  isComboboxAccessibilityStateFixEnabled:
    "SharedOnline.ChangeGate.isComboboxAccessibilityStateFixEnabled",
  onHoverIconEnabled: "SharedOnline.ChangeGate.OnHoverIconEnabled",
  react18IdBeingProcessedEnabled:
    "Microsoft.Office.SharedOnline.ChangeGate.react18IdBeingProcessedEnabled",
  isSplitButtonDisableStateFixEnabled:
    "SharedOnline.ChangeGate.splitButtonDisableStateFixEnabled",
  isRandomGradientIdEnabled:
    "SharedOnline.ChangeGate.IsRandomGradientIdEnabled",
  ...shieldExperiments,
  ...consumerExperiments,
  ...staticRibbonExperiments,
  ...fluentExperiments,
  ...unAuthExperiments,
};

export const useHighContrast = (context: ExperimentChecker) => {
  return (
    !!checkExperiment(context, ribbonExperiments.isHighContrastModeEnabled) &&
    !!checkExperiment(context, ribbonExperiments.isHighContrastThemeOn)
  );
};

export const platformFeatures = {
  disableDelayRenderToolTipMultiWindow:
    "DisableDelayRenderTooltipForMultiWindow",
  disableTaskpaneRefreshStyling: "DisableTaskpaneRefreshStyling",
  modalContextMenuIsEnabled: "ModalContextMenuIsEnabled",
  renderV9MenuAndFloatieTogetherEnabled:
    "RenderV9MenuAndFloatieTogetherEnabled",
  isSchedulingUpdateCommandUIOnMenuPositionedEnabled:
    "SchedulingUpdateCommandUIOnMenuPositionedEnabled",
  tooltipForMacIsEnabled: "TooltipForMacIsEnabled",
  cachedScalingUpAllowed: "CachedScalingUpAllowed",
};

export const cspPolicyExperiments = {
  isInnerHTMLPolicyEnabled: "InnerHtmlDomPurifyTrustedTypesPolicyEnabled",
};

export const checkExperiment = (
  context: ExperimentChecker,
  feature: string
) => {
  return checkExperimentBase(context, feature);
};

export const isShyRibbonEnabled = (context: ExperimentChecker) => {
  return isShyRibbonFeatureEnabled(context.isExperimentalFeatureEnabled);
};

export const isChatPPTEnabled = (context: ExperimentChecker) => {
  return isChatPPTFeatureEnabled(context.isExperimentalFeatureEnabled);
};

export const isShyRibbonFeatureEnabled = (
  isFeatureEnabled?: (
    feature: string,
    defaultValue?: boolean | undefined
  ) => boolean
) => {
  return isShyRibbonFeatureEnabledGeneric(isFeatureEnabled);
};

export const isChatPPTFeatureEnabled = (
  isFeatureEnabled?: (
    feature: string,
    defaultValue?: boolean | undefined
  ) => boolean
) => {
  return (
    isFeatureEnabled !== undefined &&
    isFeatureEnabled(ribbonExperiments.chatPPTEnabled)
  );
};
